import { useEffect } from 'react';

import { PATHS } from '@/shared/constants/routes/paths';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';

import { useRecordingShareModalStore } from './useRecordingShareModalStore';
import { useShareRecording } from './useShareRecording';

type EditorRecording = {
  id: string;
  status: string;
};

type RecordingShareAutoFetchOptions = {
  enabled?: boolean;
  resetOnRecordingChange?: boolean;
};

export const useRecordingShareAutoFetch = (
  recordingId: null | string,
  { enabled = true, resetOnRecordingChange = false }: RecordingShareAutoFetchOptions = {},
) => {
  const shareUrl = useRecordingShareModalStore((state) => state.shareUrl);
  const shareRecordingMutation = useShareRecording();

  useEffect(() => {
    if (!resetOnRecordingChange || !recordingId) return;

    useRecordingShareModalStore.getState().resetForRecording(recordingId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingId, resetOnRecordingChange]);

  useEffect(() => {
    if (!enabled || !recordingId) return;

    if (shareUrl) return;

    const storeState = useRecordingShareModalStore.getState();

    if (storeState.recordingId !== recordingId) return;

    if (storeState.pendingRecordingId === recordingId) return;

    const requestRecordingId = recordingId;
    useRecordingShareModalStore.getState().setPendingRecordingId(requestRecordingId);

    shareRecordingMutation
      .mutateAsync(recordingId)
      .then((response) => {
        if (useRecordingShareModalStore.getState().recordingId !== requestRecordingId) {
          return;
        }

        const newShareUrl = `${window.location.origin}${PATHS.recordingShareLink(response.sharing_id)}`;
        useRecordingShareModalStore.getState().setShareUrl(newShareUrl);
        useRecordingShareModalStore.getState().setDisabled(false);
      })
      .catch((error) => {
        if (useRecordingShareModalStore.getState().recordingId !== requestRecordingId) {
          return;
        }
        showErrorAlert(error);
      })
      .finally(() => {
        if (useRecordingShareModalStore.getState().pendingRecordingId === requestRecordingId) {
          useRecordingShareModalStore.getState().setPendingRecordingId(null);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, recordingId, shareUrl]);
};

export const useEditorShareAutoFetch = (recording: EditorRecording) => {
  useRecordingShareAutoFetch(recording.id, {
    enabled: recording.status === 'completed',
    resetOnRecordingChange: true,
  });
};
